import { type AppState, Auth0Provider, useAuth0, withAuth0 } from '@auth0/auth0-react';
import { LoadingScreen } from '@datapad/common-controls';
import { createMaterialTheme } from '@datapad/theming';
import { Button, ThemeProvider } from '@mui/material';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { connect, Provider } from 'react-redux';
import { BrowserRouter as Router, useHistory, useLocation } from 'react-router-dom';
import type { Store } from 'redux';
import RoutedDatapad from './apps/Datapad';

import { DatapadPaper } from './apps/Datapad/Components/DatapadPaper';
import './index.css';
import { createReduxStore } from './Storage';

const appTheme = createMaterialTheme();

/**
 * App with persisted (cached) redux storage.
 * Note: redux storage is stored in this component's state, as it is keyed off of the signed-in user
 * (to ensure different users on the same browser don't see eachothers' state).
 * Additionally, it is in a separate component to ensure it doesn't get thrashed (mounted and unmounted)
 * when routing locations change.
 */
function AppWithReduxStore({
	userName,
	onLogout,
}: {
	userName: string;
	onLogout: () => void;
}): React.ReactElement {
	const [store, setStore] = React.useState<Store | undefined>(undefined);

	if (store) {
		return (
			<Provider store={store}>
				<ThemeProvider theme={appTheme}>
					<ConnectedApp userName={userName} logoutFunction={onLogout} />
				</ThemeProvider>
			</Provider>
		);
	} else {
		const store = createReduxStore();
		setStore(store);
		return renderLoadingScreen('Configuring storage...');
	}
}

/**
 * App with user authentication.
 */
function AppWithAuthentication(): React.ReactElement {
	const auth0 = useAuth0();
	const location = useLocation();

	if (auth0.isLoading) {
		return renderLoadingScreen('Authenticating...');
	}

	/**
	 * Redirect function that preserves pathname and search params
	 */
	async function redirect(): Promise<void> {
		auth0.loginWithRedirect({
			appState: {
				returnTo: `${location.pathname}${location.search}`,
			},
		});
	}

	if (auth0.error) {
		return renderLoginError(auth0.error, redirect, auth0.logout);
	}

	if (auth0.isAuthenticated) {
		if (!auth0.user) {
			throw new Error('Authentication failed to provide user data.');
		}

		const userName = auth0.user.nickname as string;
		return <AppWithReduxStore userName={userName} onLogout={auth0.logout} />;
	}

	redirect();

	return renderLoadingScreen('Please sign in to continue...');
}

/**
 * Datapad app.
 * Displays various sub-apps, and offers a menu for navigating them.
 */
const ConnectedApp = connect((reduxState) => reduxState)(RoutedDatapad);

/**
 * App with auth configured
 */
function AppWithAuth(): React.ReactElement {
	const history = useHistory();

	/**
	 * Ensure that app correctly redirects on based user-specified url parameters
	 * @param appState - See {@link AppState}
	 */
	function onRedirectCallback(appState: AppState | undefined): void {
		history.replace(appState?.returnTo ?? window.location.pathname);
	}

	const InnerApp = withAuth0(AppWithAuthentication);

	return (
		<Auth0Provider
			domain="datapad.us.auth0.com"
			clientId="U0kkfC5BuX8bn9b0TEAh6DOWclZmYyv4"
			authorizationParams={{
				redirect_uri: window.location.origin,
			}}
			onRedirectCallback={onRedirectCallback}
			/**
			 * Use refresh tokens for session renewal instead of iframe-based silent
			 * authentication (`prompt=none`).
			 *
			 * Required for Auth0's "non-verifiable callback URI" migration
			 * (effective April 28, 2026). After this date, `prompt=none` requests
			 * will be rejected for non-verifiable callback URIs (e.g., localhost
			 * during development). Refresh tokens are unaffected by this change.
			 *
			 * @see {@link https://auth0.com/docs/troubleshoot/product-lifecycle/deprecations-and-migrations/migrate-to-non-verifiable-callback-uri-end-user-confirmation}
			 */
			useRefreshTokens={true}
			/**
			 * Disable the iframe-based silent authentication fallback. By default,
			 * the SDK falls back to `prompt=none` in a hidden iframe when refresh
			 * token renewal fails. Disabling this ensures the app never relies on
			 * `prompt=none`, which will be blocked for non-verifiable callback URIs
			 * after the migration deadline.
			 */
			useRefreshTokensFallback={false}
			/**
			 * Persist auth state in localStorage so that refresh tokens survive
			 * full page reloads. Without this, tokens are stored in-memory and
			 * lost on refresh, which would force a new login flow each time.
			 */
			cacheLocation="localstorage"
		>
			<InnerApp />
		</Auth0Provider>
	);
}

/**
 * Renders a themed loading screen
 */
function renderLoadingScreen(loadingScreenText: string): React.ReactElement {
	return (
		<ThemeProvider theme={appTheme}>
			<DatapadPaper>
				<LoadingScreen text={loadingScreenText} />
			</DatapadPaper>
		</ThemeProvider>
	);
}

/**
 * Renders an error message dialogue for failed login auth.
 * Offers the user the ability to retry, or to log out.
 * @param error - The error that was reported from the auth service.
 * @param loginFunction - Function to be invoked to attempt a new login.
 * @param logoutFunction - Function to be invoked to log out.
 */
function renderLoginError(
	error: Error,
	loginFunction: () => void,
	logoutFunction: () => void,
): React.ReactElement {
	return (
		<>
			<h4>There was a problem signing you in.</h4>
			<br />
			<p>{error.message}</p>
			<br />
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-around',
					width: '100%',
				}}
			>
				<Button variant="contained" onClick={() => loginFunction()}>
					Retry
				</Button>
				<Button variant="contained" onClick={() => logoutFunction()}>
					Log Out
				</Button>
			</div>
		</>
	);
}

const container = document.getElementById('root');
if (!container) {
	throw new Error('Root element not found');
}

const root = createRoot(container);
root.render(
	<React.StrictMode>
		<div
			style={{
				width: '100%',
				height: '100vh',
				color: 'white',
			}}
		>
			<Router>
				<AppWithAuth />
			</Router>
		</div>
	</React.StrictMode>,
);
